import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/evaluation.py schemas.
export interface EvalForm {
  evaluation_form_id: number;
  name: string;
  description: string;
  item_count: number;
}

export interface EvalItem {
  evaluation_item_id: number;
  evaluation_form_id: number;
  name: string;
  description: string;
  max_score: number;
  weight: number;
  section: string;
}

export interface PendingCall {
  spool_id: number;
  campaign_id: number;
  phone: string;
  disposition: string | null;
  billsec: number | null;
  extension_id: number | null;
  created_at: string | null;
}

export interface EvaluationRow {
  evaluation_id: number;
  spool_id: number;
  evaluation_form_id: number;
  form_name: string;
  evaluator_id: number | null;
  evaluator_comment: string;
  score: number;
  max_score: number;
  percent: number;
  phone: string | null;
  extension_id: number | null;
  created_at: string | null;
}

export interface AgentSummary {
  extension_id: number | null;
  calls_evaluated: number;
  avg_percent: number;
  total_score: number;
  total_max: number;
}

export interface ScoreInput {
  evaluation_item_id: number;
  score: number;
  comment: string;
}

// ---- Forms ----
export async function listForms(): Promise<EvalForm[]> {
  const resp = await api.get<APIResponse<EvalForm[]>>("/evaluation/forms");
  return unwrap(resp.data);
}

export async function createForm(body: { name: string; description: string }): Promise<number> {
  const resp = await api.post<APIResponse<{ evaluation_form_id: number }>>("/evaluation/forms", body);
  return unwrap(resp.data).evaluation_form_id;
}

export async function updateForm(formId: number, body: { name: string; description: string }): Promise<void> {
  await api.put(`/evaluation/forms/${formId}`, body);
}

export async function deleteForm(formId: number): Promise<void> {
  await api.delete(`/evaluation/forms/${formId}`);
}

// ---- Items ----
export async function listItems(formId: number): Promise<EvalItem[]> {
  const resp = await api.get<APIResponse<EvalItem[]>>(`/evaluation/forms/${formId}/items`);
  return unwrap(resp.data);
}

export interface ItemInput {
  evaluation_form_id: number;
  name: string;
  description: string;
  max_score: number;
  weight: number;
  section: string;
}

export async function createItem(body: ItemInput): Promise<number> {
  const resp = await api.post<APIResponse<{ evaluation_item_id: number }>>("/evaluation/items", body);
  return unwrap(resp.data).evaluation_item_id;
}

export async function updateItem(itemId: number, body: ItemInput): Promise<void> {
  await api.put(`/evaluation/items/${itemId}`, body);
}

export async function deleteItem(itemId: number): Promise<void> {
  await api.delete(`/evaluation/items/${itemId}`);
}

// ---- Pending calls + submit ----
export async function listPending(): Promise<PendingCall[]> {
  const resp = await api.get<APIResponse<PendingCall[]>>("/evaluation/pending");
  return unwrap(resp.data);
}

export async function submitEvaluation(body: {
  spool_id: number;
  evaluation_form_id: number;
  evaluator_comment: string;
  scores: ScoreInput[];
}): Promise<{ evaluation_id: number; score: number; max_score: number }> {
  const resp = await api.post<APIResponse<{ evaluation_id: number; score: number; max_score: number }>>(
    "/evaluation/evaluations",
    body,
  );
  return unwrap(resp.data);
}

// ---- Reports ----
export async function listEvaluations(params?: { extension_id?: number; form_id?: number }): Promise<EvaluationRow[]> {
  const resp = await api.get<APIResponse<EvaluationRow[]>>("/evaluation/evaluations", { params });
  return unwrap(resp.data);
}

export async function agentSummary(params?: { form_id?: number }): Promise<AgentSummary[]> {
  const resp = await api.get<APIResponse<AgentSummary[]>>("/evaluation/agents", { params });
  return unwrap(resp.data);
}
